import { DateTime } from 'luxon';
import { PouchDoc } from '../data-model/pouch';
import { isRecipe, isRecipeById, isLegacyMealId, Recipe, ensureRecipeTotalWeight } from './recipe';
import { ScaledImage } from './scaled-image';
import { Log_Meal } from './log-meal';
import { LogRecordType } from './user-log';
import { parseFraction } from '../util/locale-number';
import { DisplayNutrient } from './display-nutrient';
import { Gender } from '../enums';

export interface FoodMeasure {
  amount: number;
  name: string;
  weight_g: number;
  volume_ml?: number;
}

/**
 * The name of the measure that holds the serving size printed on a US-style nutrition label.
 *
 * The app writes this measure itself: the food editor creates it when the user's label style
 * is US, and its weight is edited through the serving-size field at the top of that page, not
 * through the Measures card. The Measures card therefore hides a measure with this name, so
 * that the same number is not shown in two places.
 *
 * This is a canonical English value: it is what gets saved to the database and what every
 * comparison checks, so it must never change. Only the text shown to the user is translated.
 *
 * A user must never be able to CHOOSE it for a measure of their own (#4722) -- doing so made
 * the row they were filling in disappear, and the save path then overwrote the numbers they
 * had typed with the label serving size. Same reason RECIPE_SERVINGS_MEASURE_NAME is
 * unchoosable (#4380).
 */
export const LABEL_SERVING_MEASURE_NAME = 'serving';

/** INFOODS tags used to identify nutrients.
 *
 * Nutrients can be keyed by any string, but this enum defines the most commonly used ones to make sure
 * the code doesn't accidentally contain a typo for nutrients that are handled specially.
 *
 * The key is in format "<tag>_<unit>", where "tag" is INFOODS tag
 * (http://www.fao.org/infoods/infoods/standards-guidelines/food-component-identifiers-tagnames/en/)
 * and "unit" is a measurement unit - usually g, mg, or ug, but also kcal or IU.
 *
 * In cases where INFOODS specify more than one different way to measure the same thing (e.g. PROCNA, PROCNP, PROCNT, PRO- all mean "protein"),
 * we use our custom tag with $-suffix. E.g. PRO$ in this case.
 */
export enum INFOODS {
  Calories = 'ENER$_kcal',
  KiloJoules = 'ENER$_kJ', // just a re-mapping of Calories for EU labels - DON'T SAVE!
  Protein = 'PRO$_g',
  Fat = 'FAT_g',
  CarbNet = 'CHOAVL_g',
  Fiber = 'FIB$_g',
  Cholesterol = 'CHOLE_mg',
  CarbTot = 'CHOCDF_g', // total carbs including fiber - only used in US labels - DON'T SAVE!
  Alcohol = 'ALC_g',
}

/** Atwater factors: kcal per gram for each macronutrient */
export const KCAL_PER_G_PROTEIN = 4;
export const KCAL_PER_G_FAT = 9;
export const KCAL_PER_G_CARBS = 4;
export const KCAL_PER_G_FIBER = 2;
export const KCAL_PER_G_ALCOHOL = 7;

/** Minimum daily fat in grams — at least 20% of total calories should come from fat */
export function getMinimumDailyFat_g(dailyCalories: number): number {
  return Math.round((dailyCalories * 0.2) / KCAL_PER_G_FAT);
}

/** Minimum daily carbs in grams — physiological minimum by gender */
export function getMinimumDailyCarbs_g(gender: Gender): number {
  return gender === Gender.male ? 119 : 99;
}

/**
 * Split the calories left after protein between carbs and fat by a carb/fat ratio.
 * `carbRatio01` is the share of those calories that goes to carbs, 0 to 1, the form
 * `FitnessPlan.overrideMacros.carbFatRatio` stores. The edit-nutrition-plan page shows
 * these grams and the solver plans meals for them, so both call this one rule.
 */
export function splitRemainingKcalByCarbRatio(dailyKcal: number, protein_g: number, carbRatio01: number): { carbs_g: number; fat_g: number } {
  // if protein alone uses up the calories, nothing is left for carbs or fat
  const remainingKcal = Math.max(0, dailyKcal - protein_g * KCAL_PER_G_PROTEIN);
  return {
    carbs_g: (remainingKcal * carbRatio01) / KCAL_PER_G_CARBS,
    fat_g: (remainingKcal * (1 - carbRatio01)) / KCAL_PER_G_FAT,
  };
}

/**
 * The inverse of `splitRemainingKcalByCarbRatio`: the carb share of the carb+fat calories,
 * in whole percent, as the edit-nutrition-plan slider shows it. Clamped to the slider's
 * 20-80 range, and 55 when there are no carb or fat calories to measure.
 */
export function carbFatRatioPercentFromGrams(carbs_g: number, fat_g: number): number {
  const carbKcal = carbs_g * KCAL_PER_G_CARBS;
  const fatKcal = fat_g * KCAL_PER_G_FAT;
  const total = carbKcal + fatKcal;
  if (total <= 0) return 55;
  const ratio = Math.round((carbKcal / total) * 100);
  return Math.max(20, Math.min(80, ratio));
}

/** This is used to weight importance of nutrients when calculating meal matching. */
export const infoodImportanceWeights = {
  [INFOODS.Calories]: 1,
  [INFOODS.Protein]: KCAL_PER_G_PROTEIN,
  [INFOODS.Fat]: KCAL_PER_G_FAT,
  [INFOODS.CarbNet]: KCAL_PER_G_CARBS,
  [INFOODS.Fiber]: KCAL_PER_G_FIBER,
};

/** List of nutrient amounts in a food, meal, recipe...
 * Can be per 100g, per meal, etc, depending on context.
 *
 * Keyed by INFOODs tag, as described in the INFOODS enum, though it can be any valid INFOODS string not mentioned in that enum.
 *
 * ## What a value really is
 *
 * The declared value type is `number | null`, and that is still narrower than what a map
 * read from the database can hold. In order of how often it turns up:
 *
 *  - a **number** -- the ordinary case, and the only one arithmetic can use directly;
 *  - **`null`** -- a nutrient nobody filled in. It is not zero: nobody measured it. The
 *    scaling helpers count it AS zero so a total stays a number, while
 *    `isNutrientValueEntered` answers false for it so nothing claims a measurement exists;
 *  - a **numeric string** such as `"0"` or `"3.6"` -- a record's values are JSON and a
 *    string is valid JSON. `repairNutrientsStoredAsText` turns these into numbers as a
 *    food is read;
 *  - the **marker string `"M"`** -- "nobody measured this". The same repair turns it into
 *    `null` as the food is read;
 *  - **anything else** -- `undefined`, `NaN`, an empty string, an object. No repair can
 *    invent an answer for these, so the guards below turn them into zero and report them.
 *
 * The last three reach a reader only when the map has NOT been through
 * `normalizeFoodFormat` -- a map built in memory, or a record read by some path that does
 * not normalise. That is why the guards below take `unknown` rather than this type.
 *
 * ## The type documents; it does not enforce
 *
 * `strictNullChecks` is off in both `shared/tsconfig.json` and `client/tsconfig.json`, so
 * `number | null` collapses to `number` for the compiler and it will not point at a site
 * that forgot about `null`. The widening is here to tell a READER what a value can be.
 * Use `isUsableNutrientValue`, `usableNutrientNumber` and `isNutrientValueEntered` below
 * rather than trusting the declaration, and rather than writing a fresh `|| 0`.
 */
export type Nutrients = { [key: string]: number | null };

export function checkNutrientCalories(nutrients: Nutrients): { correct: boolean, expectedCalories: number } {
  let calories = nutrients[INFOODS.Calories] || 0;
  let protein = nutrients[INFOODS.Protein] || 0;
  let fat = nutrients[INFOODS.Fat] || 0;
  let carbs = nutrients[INFOODS.CarbNet] || 0;
  let fiber = nutrients[INFOODS.Fiber] || 0;
  let alcohol = nutrients[INFOODS.Alcohol] || 0;

  let expectedCalories = protein * KCAL_PER_G_PROTEIN + fat * KCAL_PER_G_FAT + carbs * KCAL_PER_G_CARBS + (fiber || 0) * KCAL_PER_G_FIBER + (alcohol || 0) * KCAL_PER_G_ALCOHOL;
  let tolerance = expectedCalories * 0.2 + 40; // tolerance is 20% plus 40 calories as per issue #336
  let correct = true;
  // "!" because numeric comparison is false if NaNs are involved
  if (!(Math.abs(calories - expectedCalories) <= tolerance)) {
    correct = false;
  }
  return { correct, expectedCalories };
}

/** Target values for one specific nutrient. Supports two types of goals: "min < x < max" and "x ~= target".
 * At least one of min/max must be set. The other is considered unbounded. If "target" is set, then it is used
 * to display it to the user, but min/max are still used for testing (so that we can have a margin).
 */
export type NutrientTarget = {
  min?: number;
  max?: number;
  target?: number;
};

/** List of multiple nutrient targets, keyed by INFOODS tags, just like in the Nutrients type. */
export type NutrientTargets = { [key: string]: NutrientTarget };

/** Returns undefined if nutrients are empty. */
export function calculateNutrientMatch_pc(nutrients: Nutrients, targets: NutrientTargets): number | undefined {
  let totalError = 0;
  let totalTargets = 0;
  if (targets === undefined) {
    return undefined;
  }
  for (let tag of Object.keys(targets)) {
    let target = targets[tag];
    if (target == null) {
      continue;
    }
    totalTargets += (target.target || target.max || target.min || 0) * (infoodImportanceWeights[tag] || 1);

    let value = nutrients[tag] || 0;

    let err = 0;
    if (target.min && value < target.min) {
      err += target.min - value;
    } else if (target.max && value > target.max) {
      err += value - target.max;
    }
    err *= infoodImportanceWeights[tag] || 1;
    totalError += err;
  }

  if (totalTargets === 0) {
    return undefined;
  }

  return Math.min(Math.max((1 - totalError / totalTargets) * 100, 0), 100);
}

export enum FoodStatus {
  Official = 100 /** this food was generated by an officer, or marked as official by one */,
  Voted = 70 /** enough users have vouched for the correctness of this food (not implemented yet!) */,
  Contributed = 60 /** user defined food by another user */,
  Private = 50 /** generated by this user (only valid in local caches) */,
  Custom = 40 /** this is for logged nutrition from external food information source*/,
  Recent = 80 /** internally in UI to show recent meals */,
  Unknown = 0,
}

export enum FoodType {
  Food = 0,
  Meal = 1,
  Recipe = 2,
  LogMeal = 3,
}

export const foodStatusIcons: { [key: number /*FoodStatus*/]: { name: string } } = {
  [FoodStatus.Official]: { name: 'shield-checkmark' },
  [FoodStatus.Voted]: { name: 'checkmark-circle' },
  [FoodStatus.Contributed]: { name: 'people' },
  [FoodStatus.Private]: { name: 'person' },
  [FoodStatus.Custom]: { name: 'create' },
  [FoodStatus.Recent]: { name: 'none' },
  [FoodStatus.Unknown]: { name: 'help-circle-outline' },
};

export const foodTypeIcons: { [key: number /*FoodType*/]: { name: string } } = {
  [FoodType.Food]: { name: 'food' },
  [FoodType.Meal]: { name: 'meal' },
  [FoodType.Recipe]: { name: 'recipe' },
  [FoodType.LogMeal]: { name: 'time' },
};

export const foodHashSecret = 'fjT5Prg8XY0QKOKOxJeh';
export const foodDatabaseName = 'global_foods';

export interface Food extends PouchDoc {
  // full name of the food
  name: string;
  altNames?: string[]; // alternative names for the food, for better search
  status?: FoodStatus;

  /** Various free-form info, listed using double-semicolon (;;) separator. Usually Latin name of the plant/animal species, if available,
   * food class (dairy, meat, vegetables,...), origin for native foods, etc.
   */
  comments?: string;
  /* Description of the food, usually in a few sentences. Available mostly for OpenNutrition Foods. */
  description?: string;
  // category of the food
  category?: 'everyday' | 'grocery' | 'prepared' | 'restaurant';
  // additional tags
  tags?: string[]; // raw, canned, sweetened etc

  // Optional image url for the food
  image?: ScaledImage;

  /** Weight of one prepared "bulk" - used for recipes or meal templates.  */
  totalWeight_g?: number;
  /** Nutrients per 100 g, or per totalWeight if available. */
  nutrients?: Nutrients;

  // different ways to measure "portions" of the food
  measures?: FoodMeasure[];

  /** EAN or UPC codes of the food products 
    (note that one food can come in several different packagings, portions, etc - each with different codes) */
  EANs?: string[];

  /** For a record copied from an outside source: that source's own last-edit time for it, in Unix seconds
   * (Open Food Facts' `last_modified_t`). Missing on records written before #5515 - read it as unknown. */
  sourceModifiedTime?: number;
  /** For a record copied from an outside source: the mapping version it was converted with. Missing on
   * records written before #5515 - read it as older than any version. See design-docs/food-source-sync.md. */
  mappingVersion?: number;

  /** On a staple food (`food/$STP-…`) only: what it was built from, written by `tools/build-staples.js`.
   * The app does not display it yet (food staples plan 4). See design-docs/food-staples.md. */
  staple?: {
    /** the staple's key in `datasrc/foods-staples/staples.json`; never changes once uploaded */
    key: string;
    /** the group of forms it belongs to, e.g. every "Chicken breast, …" staple */
    group: string;
    /** its place inside that group, lower first */
    groupOrder: number;
    /** How often people searched for this kind of food: the highest search count in the staple's
     * group, so every form of a food ranks with its most searched form. The device ranks staple
     * suggestion rows by it (food staples plan 4c, #5625). Missing on staples uploaded before that
     * plan; read it as 0. */
    popularity?: number;
    /** every source food whose data went into it */
    sourceFoodIds: string[];
    /** The name of the picture the app shows for this staple, e.g. `chicken-breast-raw`, resolved to
     * files through `client/src/services/staple-picture-files.generated.ts` (food staples pictures
     * plan, #5694). Missing on staples uploaded before that plan, and a name the installed app has no
     * picture for reads the same way: no picture, the staple shield exactly as before. That is what
     * lets an upload and a release land in either order. */
    picture?: string;
  };
  /** The share of the food as bought that is not eaten (peel, pit, bone, shell), when a trusted source gives it.
   * Written on staples only. The amount screen shows it as a "weigh without" hint (food staples plan 4a, #5594);
   * the "Weighed with peel" checkbox there turns a weight taken with the peel into the eaten weight (#5544). */
  refuse?: {
    /** percent of the weight as bought, above 0 and below 100 */
    percent: number;
    /** what is removed, in the source database's own English words, e.g. "Stem ends, seeds and core"; for review only */
    removed?: string;
    /** what is removed, from a fixed list the app translates; most weight first. Missing on staples built before plan 4a. */
    parts?: RefusePart[];
    /** whether the percent holds for everyone or depends on how the food is bought and prepared.
     * Missing on staples built before the owner added it (2026-09-17); read it as typical. */
    precision?: RefusePrecision;
  };

  //--- the members below are used only in local cache storage for each user, then must not end up in the global database!
  local?: {
    /** data used to generate the food id, so server can generate it is legit. only stored food is uploaded to the cloud */
    hashSource?: string;
    /** whether the user likes/dislikes this food. */
    liked?: -1 | null | 1;
    /** when did the user last add this food to a meal, or recipe */
    lastUsed_utc?: string;
    lastUsedMeasureUnitName?: string;
    lastUserMeasureWeight_g?: number;
    lastUsedWeightUnit?: string;
    /** whether the user last logged this food with its "Weighed with peel / pit / bone" box ticked (#5544) */
    lastWeighedWithRefuse?: boolean;
    /** true if user has shared this recipe via link (it's published to global_foods) */
    shared?: boolean;
    /** original recipe _id in global_foods — set on imported copies, also used for re-sharing */
    sourceRecipeId?: string;
    /** original recipe _rev at import time — for Phase 2 update detection */
    sourceRevision?: string;
  };

  // Hash which contains food author id.
  authorHash?: string;
}

/**
 * How far a staple's uneaten percent can be trusted (food staples plan 4a, #5594). A rough percent is
 * still shown, but the app steers people towards weighing the food after preparing it instead.
 */
export enum RefusePrecision {
  /** the part is about the same share of every piece: an egg's shell, a peach's pit */
  Typical = 'typical',
  /** the share depends on the cut bought or on how it is trimmed or peeled: broccoli stems, fish skin */
  Rough = 'rough',
}

/**
 * A part of a food that is not eaten, as the "weigh without" hint names it (food staples plan 4a, #5594).
 * The values are stored in the staples file and on staple documents, so they never change; the app
 * translates each one. See design-docs/food-staples.md, "The staples file".
 */
export enum RefusePart {
  /** shells of eggs, nuts and shellfish, and the hulls of seeds */
  Shell = 'shell',
  Tail = 'tail',
  /** bone, and the cartilage and connective tissue that come off with it */
  Bone = 'bone',
  Skin = 'skin',
  /** separable fat trimmed off meat */
  Fat = 'fat',
  /** peel and parings */
  Peel = 'peel',
  /** the stone of a fruit */
  Pit = 'pit',
  Seeds = 'seeds',
  Core = 'core',
  /** stems, stalks, stem ends and the green caps of berries */
  Stem = 'stem',
  /** outer and damaged leaves */
  Leaves = 'leaves',
  Rind = 'rind',
  Pod = 'pod',
  Husk = 'husk',
  Cob = 'cob',
  /** tops, crowns and knobs */
  Top = 'top',
  Root = 'root',
  /** ends, trimmings, scrapings and spoiled bits cut away */
  Trimmings = 'trimmings',
}

export enum FoodSource {
  USDA = 'USDA', // USDA Standard Reference Food
  NFSA = 'NFSA', // Norwegian Food Safety Authority
  FDC = 'FDC', // FoodData Central (Branded Food)
  OFF = 'OFF', // Open Food Facts
  UDF = 'UDF', // User Defined Food
  OFR = 'OFR', // Official Recipe (ours)
  ONF = 'ONF', // OpenNutrition Food
  STP = 'STP', // Staple food, curated by us from trusted sources (design-docs/food-staples.md)
  Unknown = 'UNKNOWN',
}

/**
 * Where a food's source puts it in a search list, lower first (#5550). The full table and the
 * owner's reasons are in `design-docs/food-staples.md`, "Plan 2: tiers and dev staples".
 *
 * The first two tiers are not decided by the source at all — they are decided by how the person
 * has used the food, in `shared/src/food-search/food-search-tiers.ts`. Every other tier is a source's
 * own tier and is the `searchTier` column of {@link foodSourceTable}.
 */
export enum FoodSearchTier {
  /** a food or meal the person used recently */
  History = 1,
  /** a food, recipe or meal the person made, stored on this device */
  OwnFoods = 2,
  /** a curated staple, `food/$STP-…` */
  Staples = 3,
  /** USDA, ONF, NFSA, FDC and our official recipes */
  Trusted = 4,
  /** other people's foods found by the server, and ids with an unknown prefix */
  Contributed = 5,
  /** Open Food Facts, `food/$OFF-…` */
  OpenFoodFacts = 6,
}

// The long name and the citation each source used to carry moved to the client
// (`client/src/pages/edit-food/food-source-display.ts`) in #4366. They are read
// only by the Edit Food source dialog, and two of them are app copy that has to
// come from the translation catalog, which shared code cannot reach.
export interface FoodSourceInfo {
  source: FoodSource;
  icon: string;
  shortName: string;
  trustLevel: 'official' | 'crowdsource' | 'community';
};

/**
 * Everything the app decides about a food purely from which database it came out of.
 *
 * This is the one place those facts are written down. Until this table existed the same source was
 * described in seven separate places — the enum, an id-prefix chain, a display table, a scoring
 * table and three tier switches — and registering the staples source (#5550) had to edit every one
 * of them. None of the seven failed to compile when one was forgotten: an unregistered source
 * silently read as Unknown, scored 0, and sorted into the Contributed tier.
 */
export interface FoodSourceRow extends FoodSourceInfo {
  /**
   * Every document-id prefix that belongs to this source. Checked with `startsWith` against the
   * whole id. Empty for Unknown, which is not matched but is what is left when nothing else matches.
   */
  idPrefixes: string[];
  /**
   * What `calculateFoodScoreBoost` adds for a food from this source, before the 0.25 scaling every
   * score gets. These numbers also reach the search server: they are baked into each food's stored
   * `score_boost` field, so changing one reorders live search results. `design-docs/food-staples.md`
   * records the measured effect of the staple value.
   */
  scoreBoostPoints: number;
  /** where a food from this source sits in a search list; see {@link FoodSearchTier} */
  searchTier: FoodSearchTier;
}

/**
 * One row per food source. Adding a source means adding a value to {@link FoodSource} and a row
 * here; leaving the row out is a compile error, because the key type covers the whole enum.
 */
export const foodSourceTable: { [source in FoodSource]: FoodSourceRow } = {
  [FoodSource.USDA]: {
    source: FoodSource.USDA,
    idPrefixes: ['food/$USDA-'],
    icon: foodStatusIcons[FoodStatus.Official].name,
    shortName: 'USDA',
    trustLevel: 'official',
    scoreBoostPoints: 10,
    searchTier: FoodSearchTier.Trusted,
  },
  [FoodSource.NFSA]: {
    source: FoodSource.NFSA,
    idPrefixes: ['food/$NFSA-'],
    icon: foodStatusIcons[FoodStatus.Official].name,
    shortName: 'NFSA',
    trustLevel: 'official',
    scoreBoostPoints: 20,
    searchTier: FoodSearchTier.Trusted,
  },
  [FoodSource.FDC]: {
    source: FoodSource.FDC,
    idPrefixes: ['food/$FDC-'],
    icon: foodStatusIcons[FoodStatus.Official].name,
    shortName: 'FDC',
    trustLevel: 'official',
    scoreBoostPoints: 6,
    searchTier: FoodSearchTier.Trusted,
  },
  [FoodSource.OFF]: {
    source: FoodSource.OFF,
    idPrefixes: ['food/$OFF-'],
    icon: foodStatusIcons[FoodStatus.Official].name,
    shortName: 'OFF',
    trustLevel: 'crowdsource',
    // Open Food Facts is the only source scored below zero: anyone on the internet can type a
    // record into it, so it ranks under every food we have a reason to trust.
    scoreBoostPoints: -6,
    searchTier: FoodSearchTier.OpenFoodFacts,
  },
  [FoodSource.OFR]: {
    source: FoodSource.OFR,
    // $OFI is a small set of manually created foods used in $OFR recipes
    idPrefixes: ['food/$OFR-', 'food/$OFI-'],
    icon: foodStatusIcons[FoodStatus.Official].name,
    shortName: 'Recipe',
    trustLevel: 'official',
    scoreBoostPoints: 20,
    searchTier: FoodSearchTier.Trusted,
  },
  [FoodSource.UDF]: {
    source: FoodSource.UDF,
    idPrefixes: ['food/+UDF-'],
    icon: foodStatusIcons[FoodStatus.Contributed].name,
    shortName: 'Community',
    trustLevel: 'community',
    scoreBoostPoints: 8,
    // this is the tier somebody ELSE's food gets. The person's own copy is lifted to OwnFoods by
    // `sourceTierOf`, which asks whether the food is stored on this device before it asks the source.
    searchTier: FoodSearchTier.Contributed,
  },
  [FoodSource.ONF]: {
    source: FoodSource.ONF,
    idPrefixes: ['food/$ONF-'],
    icon: foodStatusIcons[FoodStatus.Official].name,
    shortName: 'ONF',
    trustLevel: 'official',
    // a bit above NFSA, but low enough that if it has EAN, then NFSA is preferred
    scoreBoostPoints: 24,
    searchTier: FoodSearchTier.Trusted,
  },
  [FoodSource.STP]: {
    source: FoodSource.STP,
    idPrefixes: ['food/$STP-'],
    icon: foodStatusIcons[FoodStatus.Official].name,
    shortName: 'Staple',
    trustLevel: 'official',
    // curated staples lead the server's results too, well above ONF (#5550)
    scoreBoostPoints: 40,
    searchTier: FoodSearchTier.Staples,
  },
  [FoodSource.Unknown]: {
    source: FoodSource.Unknown,
    idPrefixes: [],
    icon: '',
    shortName: '',
    trustLevel: undefined,
    scoreBoostPoints: 0,
    searchTier: FoodSearchTier.Contributed,
  },
};

/**
 * The Edit Food screen's view of {@link foodSourceTable}: the icon, short name and trust level,
 * without the ranking and search facts that screen has no use for.
 */
export const foodSourceInfoTable: { [key: string]: FoodSourceInfo } = {};
for (const row of Object.values(foodSourceTable)) {
  foodSourceInfoTable[row.source] = {
    source: row.source,
    icon: row.icon,
    shortName: row.shortName,
    trustLevel: row.trustLevel,
  };
}

/** which database a food came out of, read from its document id; Unknown when no prefix matches */
export function getFoodSource(foodId: string): FoodSource {
  for (const row of Object.values(foodSourceTable)) {
    if (row.idPrefixes.some(prefix => foodId.startsWith(prefix))) {
      return row.source;
    }
  }
  return FoodSource.Unknown;
}

/**
 * Where a food's source alone puts it in a search list. It does not know what the person has done
 * with the food, so it never answers History or OwnFoods — `sourceTierOf` in
 * `shared/src/food-search/food-search-tiers.ts` adds those, and the search replay in `tools/` reads
 * this straight, because nothing it measures belongs to any one person.
 */
export function searchTierOfFoodId(foodId: string): FoodSearchTier {
  return foodSourceTable[getFoodSource(foodId)].searchTier;
}

/**
 * Where a food comes from, as far as the user needs to know when deciding whether to trust it.
 * Drawn on every food card (staple icon, shield, person, "Community", "Open Food Facts") and, from Add Foods phase 3,
 * used by the Hide Unverified filter, which hides exactly `MemberFood` and `OpenFoodFacts` - a staple counts as
 * verified there, like any trusted database. See design-docs/add-foods-page.md and design-docs/food-staples.md.
 */
export enum FoodOrigin {
  /** a curated staple (`food/$STP-`); a trusted food for filtering, drawn with its own icon (food staples plan 4a) */
  Staple = 'Staple',
  /** an official database we trust (USDA, NFSA, FDC, ONF, our own recipes) */
  TrustedDatabase = 'TrustedDatabase',
  /** the user's own food, recipe or meal, whether kept private or shared */
  OwnFood = 'OwnFood',
  /** a food or recipe another member added; nobody checked it */
  MemberFood = 'MemberFood',
  /** an Open Food Facts product, which anyone on the internet can type in */
  OpenFoodFacts = 'OpenFoodFacts',
  /** no source to show: no id, a logged meal, a typed-in ingredient, an unknown prefix */
  None = 'None',
}

/**
 * The one place that decides a food's `FoodOrigin`. Keep every rule here, so the card and the
 * filters never disagree.
 *
 * Rules, checked in order:
 * 0. no food, or no id → None. Typed-in ingredients have no id and unmatched scanner rows an empty one.
 * 1. status Private → OwnFood. Status Private is the only sign a food is the user's own, and it
 *    survives publishing and covers an imported recipe copy.
 * 2. a staple id (`food/$STP-`) → Staple. It comes before the trust level, which says official
 *    for staples too.
 * 3. the source table's trust level: official → TrustedDatabase, crowdsource → OpenFoodFacts,
 *    community → MemberFood. The status is not used here: every `food/$` id reads as Official,
 *    Open Food Facts included.
 * 4. a `food/+UDR-` recipe that is not Private → MemberFood (`getFoodSource` does not know this prefix).
 * 5. anything else → None.
 */
export function getFoodOrigin(food: { _id?: string; status?: FoodStatus } | undefined): FoodOrigin {
  // if there is nothing to classify, show nothing
  if (!food || typeof food._id !== 'string' || food._id === '') {
    return FoodOrigin.None;
  }
  if (food.status === FoodStatus.Private) {
    return FoodOrigin.OwnFood;
  }
  const source = getFoodSource(food._id);
  if (source === FoodSource.STP) {
    return FoodOrigin.Staple;
  }
  switch (foodSourceInfoTable[source]?.trustLevel) {
    case 'official':
      return FoodOrigin.TrustedDatabase;
    case 'crowdsource':
      return FoodOrigin.OpenFoodFacts;
    case 'community':
      return FoodOrigin.MemberFood;
  }
  if (food._id.startsWith('food/+UDR-')) {
    return FoodOrigin.MemberFood;
  }
  return FoodOrigin.None;
}

/*
 * The three guards for a stored nutrient value live here, together, because they answer
 * three DIFFERENT questions about the same value and the difference is easy to lose:
 *
 *  - `isNutrientValueEntered` -- did a human actually measure this? `null` is NO.
 *  - `isUsableNutrientValue`  -- can the scaling arithmetic use this? `null` is YES; it
 *    means zero, and every incomplete food in the database has some.
 *  - `usableNutrientNumber`   -- the number to multiply with.
 *
 * `null` answering differently to the first two is the whole point of having both. A
 * nutrient nobody filled in must not be displayed as a measurement, and must not be
 * reported as a fault either -- there are far too many of them for that to be news.
 *
 * All three take `unknown` rather than the `Nutrients` value type on purpose. They exist
 * precisely for the values that type does not model: see the `Nutrients` comment above,
 * and note that `strictNullChecks` is off here so the declaration protects nobody.
 */

/**
 * Did somebody actually measure this nutrient? Only a real, finite number counts.
 *
 * `null` in particular does NOT. It is what a nutrient nobody measured stores, and what the
 * marker `"M"` is repaired to as a food is read -- but `isFinite(null)` is `true`, so a bare
 * `isFinite` check reads "nobody measured this" as "somebody measured zero".
 */
export function isNutrientValueEntered(value: unknown): boolean {
  return typeof value === 'number' && isFinite(value);
}

/**
 * Is this stored nutrient value one the scaling helpers can compute with?
 *
 * One statement of what "unusable" means, so the helpers using it cannot drift apart
 * on it. Two things are usable:
 *
 *  - a real number, which is the ordinary case;
 *  - `null`, which is ordinary too. A food record read from the database may
 *    store `null` for a nutrient nobody filled in, and zero is the answer either
 *    way -- so calling it usable changes no number, and there is nothing to
 *    report. Treating it as a problem would file a Sentry event for every
 *    incomplete food in the database.
 *
 * Everything else is unusable: `undefined` (a key explicitly present but unset),
 * `NaN`, a string, an object. Each of those is turned into zero by
 * `usableNutrientNumber` below, which is the right answer on screen and the thing
 * nobody could see.
 *
 * `Infinity` is deliberately NOT called unusable. It is a number, the guards do
 * not turn it into zero, and the total it produces is visibly wrong rather than
 * quietly short -- a different problem from the one this reports.
 */
export function isUsableNutrientValue(value: unknown): boolean {
  if (value === null) return true;
  return typeof value === 'number' && !Number.isNaN(value);
}

/**
 * The number to compute with for a stored nutrient value: the value itself when it
 * is one, and zero when it is not.
 *
 * This is the other half of `isUsableNutrientValue` and the two MUST agree: the
 * predicate decides what gets reported, and this decides what gets multiplied. A
 * value the predicate calls unusable and this one let through would be reported and
 * then poison the arithmetic anyway.
 *
 * Keeping them apart is what this replaced. Every guard used to be `stored || 0`,
 * which only zeroes a FALSY value -- so `undefined`, `NaN` and `''` became zero
 * while a truthy non-number went straight into the multiplication and made the
 * result NaN. Real food records store the marker string `"M"` for a nutrient nobody
 * measured, so that case is not hypothetical; `repairNutrientsStoredAsText` turns
 * that marker into `null` as a food is read.
 *
 * `null` gives zero, which is what `|| 0` gave it, and `Infinity` is handed back
 * untouched -- both as `isUsableNutrientValue` describes.
 *
 * The second check below looks redundant and is not: `isUsableNutrientValue` also
 * answers true for `null`, so it cannot be written as a `value is number` predicate,
 * and `typeof value === 'number'` is what keeps `null` out of the branch that returns
 * the value itself. Making it a predicate instead would mean dropping the `null` arm,
 * which would file a Sentry event for every incomplete food in the database.
 */
export function usableNutrientNumber(value: unknown): number {
  return isUsableNutrientValue(value) && typeof value === 'number' ? value : 0;
}

/**
 * True when an entry carries no energy information at all: `nutrients` is missing, or calories,
 * protein, fat and net carbs are all missing or not finite numbers. A stated 0 counts as a value.
 * These are the keys `getNutrientConsistency` checks; `CarbTot` is deliberately not used.
 */
export function hasNoNutritionData(nutrients?: Nutrients): boolean {
  if (nutrients == null) {
    return true;
  }
  const keys = [INFOODS.Calories, INFOODS.Protein, INFOODS.Fat, INFOODS.CarbNet];
  return keys.every(key => !isNutrientValueEntered(nutrients[key]));
}

/**
 * How far apart the stated and macro-derived calories must be before the app says so to the
 * user. Both conditions have to hold. They are deliberately much stricter than
 * `checkNutrientCalories`' own tolerance, which was tuned for warning somebody who is typing
 * values and can correct them; these govern accusing a database record the user cannot edit.
 * Why each threshold is where it is, is written at the point it is applied below. See #4541.
 */
const GROSS_MISMATCH_MIN_RATIO = 2;
/** Per 100 g. Below this the two figures are too close for their ratio to mean anything. */
const GROSS_MISMATCH_MIN_DIFFERENCE_KCAL = 50;

/**
 * How well a set of nutrients agrees with the calories stated alongside them.
 *
 * Two different consumers read this, and they deliberately stop at different tiers, so
 * do not collapse them into one boolean. Search ranking penalises `Mismatch` or worse,
 * which is the long-standing loose rule. The user-visible warning fires only on
 * `GrossMismatch`, because a badge on a record the user cannot edit is an accusation and
 * has to be right. See #4541.
 */
export enum NutrientConsistency {
  /** the stated calories agree with the macros recorded next to them */
  Consistent = 'Consistent',
  /** no protein, fat or carbohydrate was recorded, so there is nothing to check against */
  Unverifiable = 'Unverifiable',
  /** outside `checkNutrientCalories`' tolerance of 20% plus 40 kcal */
  Mismatch = 'Mismatch',
  /** so far out that the record cannot be a rounding or labelling difference */
  GrossMismatch = 'GrossMismatch',
}

/**
 * Work out how badly `nutrients` disagrees with its own stated calories.
 *
 * Takes nutrients PER 100 g, not a `Food`. Recipes store their nutrients per
 * `totalWeight_g`, and the absolute-difference floor below is not scale-invariant, so
 * normalising is the caller's job and is left visible at the call site on purpose.
 */
export function getNutrientConsistency(nutrients: Nutrients): NutrientConsistency {
  if (nutrients == null) {
    return NutrientConsistency.Unverifiable;
  }
  const n = nutrients;

  // if not one of the three energy-bearing macros was recorded, there is nothing to
  // compare the calories against. this matches the guard the ranking penalty has always
  // used, so ranking behaviour does not move.
  if (n[INFOODS.Protein] == null && n[INFOODS.Fat] == null && n[INFOODS.CarbNet] == null) {
    return NutrientConsistency.Unverifiable;
  }

  const { correct, expectedCalories } = checkNutrientCalories(n);
  if (correct) {
    return NutrientConsistency.Consistent;
  }

  // from here the record is outside the loose tolerance. everything below decides whether it
  // is far enough out to say so to the user's face.

  // if any one of the four is missing, the expected figure was computed from an incomplete
  // record: checkNutrientCalories reads a missing macro as zero, so a food listing calories
  // and protein alone always looks wrong when nothing about it need be.
  const statedCalories = n[INFOODS.Calories];
  const hasEveryMacro = statedCalories != null
    && n[INFOODS.Protein] != null && n[INFOODS.Fat] != null && n[INFOODS.CarbNet] != null;
  if (!hasEveryMacro) {
    return NutrientConsistency.Mismatch;
  }

  // a figure that is not a finite number cannot be compared with anything. a recipe whose
  // stored total weight is zero scales its nutrients to Infinity on the way in here, and
  // every test below answers false for Infinity and NaN alike -- so without this the code
  // falls through to the worst verdict on data that says nothing, and the warning would
  // offer the user a calorie figure of Infinity. doubtful, not damning.
  if (!isFinite(statedCalories) || !isFinite(expectedCalories)) {
    return NutrientConsistency.Mismatch;
  }

  // if the two figures are close in absolute terms, the ratio between them means nothing.
  // this is what keeps a 1 kcal diet drink, whose macros imply 0.4, off the list.
  if (Math.abs(statedCalories - expectedCalories) < GROSS_MISMATCH_MIN_DIFFERENCE_KCAL) {
    return NutrientConsistency.Mismatch;
  }

  // and if they are within a factor of two, the gap is explainable by the honest reasons a
  // label disagrees with our arithmetic -- alcohol measured by volume, sugar alcohols burned
  // at less than 4 kcal per gram, fibre counted differently by whoever typed the label. the
  // corruptions worth accusing a record of are all far past this: kilojoules entered as
  // calories is a factor of 4.18, a misplaced decimal point 10 or 100, per-serving figures
  // entered per 100 g typically 2 to 5.
  //
  // a zero on either side makes that ratio meaningless, and the two zeroes mean opposite
  // things, so they are handled separately rather than lumped together.

  // if the macros work out to no energy at all, they say nothing about whether the stated
  // calories are wrong. the food's energy simply comes from something this record does not
  // carry -- alcohol is the everyday case, and Open Food Facts often omits it, so a correct
  // spirits record reads as 0 g of protein, fat and carbohydrate beside a few hundred
  // calories. accusing that record would make the badge depend on whether an optional field
  // happened to be filled in.
  if (expectedCalories <= 0) {
    return NutrientConsistency.Mismatch;
  }

  // the other direction has no innocent reading. the macros account for real energy -- at
  // least the 50 kcal the floor above required -- while the record claims none, which is a
  // missing or zeroed energy field rather than an unrecorded nutrient.
  if (statedCalories <= 0) {
    return NutrientConsistency.GrossMismatch;
  }

  if (Math.max(statedCalories, expectedCalories) / Math.min(statedCalories, expectedCalories)
      < GROSS_MISMATCH_MIN_RATIO) {
    return NutrientConsistency.Mismatch;
  }

  return NutrientConsistency.GrossMismatch;
}

/**
 * The calories a food's own macros work out to, but ONLY when the food is a
 * `GrossMismatch` -- i.e. only when the app is prepared to tell the user the record looks
 * wrong. Undefined for everything else.
 *
 * This exists so a caller can ask one question instead of two (which tier is it, and what
 * should the message say), and so the tier a user-visible warning keys on is decided in one
 * place rather than at each screen that shows one. Nutrients are PER 100 g, as for
 * `getNutrientConsistency`.
 */
export function getGrossMismatchExpectedCalories(nutrients: Nutrients): number | undefined {
  if (getNutrientConsistency(nutrients) !== NutrientConsistency.GrossMismatch) {
    return undefined;
  }
  // rounded because this is shown to a person as an approximation, never calculated with
  return Math.round(checkNutrientCalories(nutrients).expectedCalories);
}

export function calculateFoodScoreBoost(food: Food): number {
  if (food == null || food.nutrients == null) {
    return -100;
  }
  let score = 0;
  const scoreScaler = 0.25; // we tune this to make this compatible with the ElasticSearch text search score

  // determine source-based score
  score += foodSourceTable[getFoodSource(food._id)].scoreBoostPoints;

  // if food is a recipe
  if (isRecipe(food)) {
    // then this is all the scoring we do - don't do any macro or keyword boosts/penalties
    return score * scoreScaler;
  }

  const n = food.nutrients;

  // if has all macros
  const macros: string[] = [INFOODS.Calories, INFOODS.Protein, INFOODS.Fat, INFOODS.CarbNet];
  if (macros.every(macro => n[macro] != null)) {
    score += 2;
  }
  // boost +1 for fiber and cholesterol _each_
  const extraMacros: string[] = [INFOODS.Fiber, INFOODS.Cholesterol];
  score += extraMacros.filter(macro => n[macro] != null).length;

  // count other nutrients that are not accounted for above
  const otherNutrientCount = Object.keys(n)
    .filter(k => !macros.includes(k) && !extraMacros.includes(k) && n[k] != null).length;
  // boost for having at least 2
  if (otherNutrientCount >= 2) {
    score += 1;
  }
  // mega boost for having at least 10
  if (otherNutrientCount >= 10) {
    score += 1;
  }

  // add value-based adjustments only for foods with calories (so we don't overboost things like salt, water...)
  if (n[INFOODS.Calories] > 0) {
    // lower calories is better
    if (n[INFOODS.Calories] != null) {
      score += (500 - n[INFOODS.Calories]) / 100; // maps chocolate to 0, pure fat to -4, water to 5
    }
    // lower carbs is better
    if (n[INFOODS.CarbNet] != null) {
      score += (100 - n[INFOODS.CarbNet]) / 20; // maps sugar to 0, keto to 5
    }
    // higher protein is better
    if (n[INFOODS.Protein] != null) {
      score += n[INFOODS.Protein] / 20; // maps pure protein to 5, water to 0
    }
  }

  // penalize calories that the food's own macros contradict. this uses the LOOSE tier on
  // purpose -- the user-visible warning added in #4541 uses the strict one, because
  // de-ranking a doubtful record is cheap and accusing it in front of the user is not.
  const consistency = getNutrientConsistency(food.nutrients);
  if (consistency === NutrientConsistency.Mismatch || consistency === NutrientConsistency.GrossMismatch) {
    score = -10;
  }

  // mega-penalty for missing calories
  if (n[INFOODS.Calories] == null) {
    score = -100;
  }

  // no nutritions can ever be negative
  let negativeValues = Object.values(n).filter(v => v < 0).length > 0;
  // if total of all macros is >100g - that's impossible!
  let totalMacros = n[INFOODS.Protein] + n[INFOODS.Fat] + n[INFOODS.CarbNet] + n[INFOODS.Fiber];
  let above100g = totalMacros > 100;

  // mega-penalty for having impossible values of any kind
  if (above100g || negativeValues) {
    score = -100;
  }

  // keyword-based boosts
  // split into words (and remove special characters)
  const lname = food.name.toLowerCase().split(/\W+/);
  if (lname.includes('raw')) {
    score += 3;
  }
  if (lname.includes('ripe')) {
    score += 1;
  }
  if (lname.includes('whole')) {
    score += 1;
  }
  if (lname.includes('fresh')) {
    score += 1;
  }
  // if it ends with "unspecified" suffix give a big boost
  if (lname[lname.length - 1] === 'unspecified') {
    score += 10;
  }

  return score * scoreScaler;
}

/** Simplified data about a food, used for faster transfer in searches.
 * Uses _id (not id) for compatibility with Food which extends PouchDoc.
 */
export interface FoodHeader {
  /** stores reference to complete food - if available - in internal use */
  cachedFood?: AnyFood;
  // used temporarily in UI for searching
  match?: number;
  /** how many of the typed words this food's names answered at all; used temporarily when the
   *  food list puts its results in order (#5410) */
  answeredWords?: number;
  /** how many of those it answered with no letter changed; orders the foods that answered the
   *  same amount, ahead of how recently the food was logged (#5410) */
  exactWords?: number;
  /** how many words of its name answer none of the typed words; fewer goes first among foods that
   *  answered equally well, ahead of recency (#4970). Set only for a typed or barcode search;
   *  unset, every food ties on it */
  extraWords?: number;
  /** the food's source tier, lower first (`shared/src/food-search/food-search-tiers.ts`); orders foods
   *  that answered equally well, ahead of extra words (#5550). Set only while the search box is not
   *  empty */
  tier?: number;
  /** the other names the server searched, copied from a server hit so the food list can see which
   *  name matched (food staples plan 3). Unset on foods read from the device, whose names are on
   *  `cachedFood`. One exception: a recently logged meal carries the names of the foods inside it
   *  here, so a search for one of them finds the meal (#5246, `recentMealSearchNames`) */
  altNames?: string[];
  // score we got from ElasticSearch
  score?: number;
  elasticHit?: any; // for easier debugging
  /** when was this food last used */
  age?: number;
  /** uses _id (not id) for compatibility with Food/PouchDoc */
  _id: string;
  name: string;
  description?: string;
  displayNutrients?: DisplayNutrient[];
  status?: FoodStatus;
  liked?: -1 | null | 1;
  recipe?: boolean;
  meal?: boolean;
}

export function isLogMealById(foodId: string): boolean {
  return foodId.startsWith('log/');
}

export type ComplexFood = Recipe | Log_Meal;

export function isComplexFood(item: AnyFood): item is ComplexFood {
  if (isLogMeal(item)) {
    return true;
  } else {
    return isRecipe(item);
  }
}

export function isComplexFoodById(foodId: string): boolean {
  return isRecipeById(foodId) || isLogMealById(foodId);
}

export type AnyFood = ComplexFood | Food;

// Re-export isLegacyMealId for convenience (used for publishing restrictions)
export { isLegacyMealId };

export function isLogMeal(item: AnyFood): item is Log_Meal {
  return item && 'type' in item && item.type === LogRecordType.Meal;
}

// this abstracts the fact that Log_Meal is not based on the same class as all else
export function getAnyFoodUsedDate(food: AnyFood): string | undefined {
  if (isLogMeal(food)) {
    return food.time_utc;
  }
  return food.local && food.local.lastUsed_utc;
}

// true when this food is one the user has actually logged/used before: it carries a
// `lastUsed_utc` (written only on real logging, not on plain caching or liking). Meals
// are excluded here — they are surfaced and badged separately as "recent meals", and
// `getAnyFoodUsedDate` would otherwise always report a meal as used (via `time_utc`).
export function hasFoodBeenLoggedBefore(food: AnyFood | undefined): boolean {
  // if there is no cached food, or it is a meal, it does not get the "logged before" mark
  if (!food || isLogMeal(food)) {
    return false;
  }
  return !!getAnyFoodUsedDate(food);
}

/**
 * The last-used date a food carries once the user has taken it out of their history from the Add
 * Foods card menu (owner, 2026-09-20).
 *
 * The food is not deleted and not un-cached: it keeps working offline and a search still finds it.
 * The date is simply stamped far enough back that the ranking puts it below every food the user
 * really has eaten, so it leaves the top of the list Add Foods shows before anything is typed.
 *
 * A fixed old date rather than removing `lastUsed_utc` altogether: the "Logged Before" filter, the
 * recent-foods reads and the search ranking all ask whether that field is there, and removing it
 * would make the food behave as one the user had never logged at all. Hiding one entry from the
 * top of a list is a smaller thing than that, and this is what the owner asked for.
 */
export const FOOD_FORGOTTEN_LAST_USED_UTC = '1970-01-01T00:00:00.000Z';

/** whether this food has already been taken out of the history (see the constant above) */
export function isFoodForgottenFromHistory(food: AnyFood | undefined): boolean {
  return !!food && getAnyFoodUsedDate(food) === FOOD_FORGOTTEN_LAST_USED_UTC;
}

/**
 * Whether the Add Foods card menu offers "Remove from History" for this food.
 *
 * It is offered for a food that IS in the history and is not already out of it. A logged meal
 * never qualifies: `hasFoodBeenLoggedBefore` excludes meals, whose place in the list comes from
 * when they were eaten rather than from a last-used stamp.
 */
export function canForgetFoodFromHistory(food: AnyFood | undefined): boolean {
  return hasFoodBeenLoggedBefore(food) && !isFoodForgottenFromHistory(food);
}

export function generateUserFoodId(userId: string, food: Food, prefix: string, crypto: any) {
  let time = DateTime.utc().toISO();

  food.local = food.local || {};

  food.local.hashSource = userId + ':' + time;

  let hash = crypto.createHmac('SHA1', foodHashSecret).update(food.local.hashSource).digest('hex');
  food._id = `food/+${prefix}-${hash}`;
}

/**
 * Whether `food` is this user's own food, stamped by `generateUserFoodId` above, and so
 * one this user may publish (#3255).
 *
 * Only the author stamp counts. A food with NO stamp is never treated as owned, unlike
 * `isOwnedCustomExercise`'s legacy case: `publishFood_impl` skips its ownership check for
 * such a food and writes it under the publishing user's name, so publishing one could put
 * somebody else's food out as this user's.
 */
export function isOwnedUserFood(food: Food | null | undefined, uid: string | undefined): boolean {
  const hashSource = food?.local?.hashSource;
  return !!uid && typeof hashSource === 'string' && hashSource.split(':')[0] === uid;
}

export function getShortFoodName(longFoodName: string): string {
  return (longFoodName.match(/^([^,\(]+)/) || [longFoodName])[0];
}

export function getIngredientText(
  ingredients: Array<{ name: string }> | undefined,
  separator: string = ', '
): string {
  if (!ingredients?.length) return '';
  // the ingredients usually come from a stored meal or recipe that an older or broken app
  // version may have written, so an entry with no name, or a name that is not text, is
  // skipped rather than allowed to throw and take the whole list down with it
  return ingredients
    .filter(i => typeof (i as { name?: unknown } | null | undefined)?.name === 'string')
    .map(i => getShortFoodName(i.name))
    .join(separator);
}

/**
 * Get the reference weight for nutrient scaling.
 * - For regular foods: nutrients are per 100g, so returns 100
 * - For recipes: nutrients are per totalWeight_g, so returns that value
 *
 * Note: For recipes, ensureRecipeTotalWeight() should be called first to guarantee
 * totalWeight_g is set. The fallback to 100 is a safety measure.
 */
export function getFoodReferenceWeight(food: Food): number {
  if (isRecipe(food)) {
    return food.totalWeight_g || 100;
  }
  return 100;
}

/**
 * Apply standard food format fixes:
 * - Ensure recipe totalWeight_g is set correctly
 * - Remove totalWeight_g=0 for non-recipes
 * - Turn a nutrient stored as text -- a numeric string, or the not-measured marker -- into
 *   the value it stands for
 * - Clean up empty measures
 * - Normalize measures where name starts with a number (e.g., "1 medium" → amount=1, name="medium")
 */
export function normalizeFoodFormat(food: Food): void {
  // if it's a recipe, ensure totalWeight_g is set correctly
  if (isRecipe(food)) {
    ensureRecipeTotalWeight(food);
  } else if (food.totalWeight_g === 0) {
    // non-recipes shouldn't have totalWeight_g set to 0
    food.totalWeight_g = undefined;
  }

  repairNutrientsStoredAsText(food);

  if (!food.measures) return;

  // remove any empty measures
  food.measures = food.measures.filter(m => m.name && m.weight_g);

  // normalize measures where name starts with a number (e.g., "1 medium" → amount=1, name="medium")
  // pattern: number part (digits, spaces for mixed fractions, slashes, dots) followed by word part (starts with letter, 2+ chars)
  const leadingNumberPattern = /^([\d\s./]+)\s+([a-zA-Z][a-zA-Z].*)$/;
  for (const measure of food.measures) {
    const match = measure.name.match(leadingNumberPattern);
    if (match) {
      const leadingNumber = parseFraction(match[1].trim(), {});
      // only apply if we got a valid positive number
      if (leadingNumber > 0 && isFinite(leadingNumber)) {
        const cleanName = match[2];
        // multiply with existing amount (default to 1 if not set)
        measure.amount = (measure.amount || 1) * leadingNumber;
        measure.name = cleanName;
      }
    }
  }
}

/** What these food records store for a nutrient nobody measured. */
const NUTRIENT_NOT_MEASURED_MARKER = 'M';

/**
 * Turn a nutrient value stored as text into the value it stands for.
 *
 * Some food records really do store a nutrient as a string, so a reader that INSPECTS
 * the value rather than multiplying by it sees damaged data where the arithmetic has
 * always been fine. A string spelling a finite number becomes that number, and the
 * marker `"M"` becomes null -- the repo owner settled on 2026-09-22 that it means
 * "nobody measured this". Every other string is left alone so it still reaches the
 * guard in `ingredient.ts` and is still reported. Which records do this, and who
 * settled what `"M"` means:
 * `design-docs/deferred-findings/2026-09-21T1122Z-marker-string-nutrient-meaning.md`, and
 * `design-docs/deferred-findings/2026-09-21T0900Z-repair-string-nutrients-on-read.md`.
 */
function repairNutrientsStoredAsText(food: Food): void {
  const nutrients = food.nutrients;
  if (!nutrients || typeof nutrients !== 'object') return;

  for (const nutrientKey of Object.keys(nutrients)) {
    const stored: unknown = nutrients[nutrientKey];
    if (typeof stored !== 'string') continue;

    // an empty or blank string spells no number at all -- Number('') is 0, which would
    // invent a measurement the record never made, so it is left for the guard to report
    const trimmed = stored.trim();
    if (trimmed === '') continue;

    // "nobody measured this" becomes null -- exactly what a nutrient nobody filled in
    // already stores -- so the guard stops reporting the key. This DOES change what the
    // arithmetic produces on a line without cf9fb0dc5: there `("M" || 0) * w` is NaN and
    // `0 + ("M" || 0)` is the string "0M", where null gives a plain 0.
    if (trimmed === NUTRIENT_NOT_MEASURED_MARKER) {
      nutrients[nutrientKey] = null;
      continue;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) continue;

    nutrients[nutrientKey] = parsed;
  }
}
